import { ZentaraMark } from "@/components/Logo";

const COLS = [
  ["received", "Received"], ["working_on", "Working On"], ["checking", "Checking"],
  ["completed", "Completed"], ["requires_attention", "Attention"], ["returned", "Returned"],
];

function Section({ n, title, children }) {
  return (
    <section className="mt-7">
      <h2 className="font-heading text-[15px] font-bold uppercase tracking-wide text-neutral-900">
        {n}. {title}
      </h2>
      <div className="mt-2 border-t-2 border-[#c5a24a] pt-3 text-[13px] text-neutral-700">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  if (!items || !items.length) return <p className="text-neutral-500">N/A</p>;
  return <ul className="list-disc space-y-1 pl-5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>;
}

export function ReportDocument({ report }) {
  const r = report;
  return (
    <div className="report-paper mx-auto w-full max-w-[8.5in] bg-white px-10 py-10 text-neutral-800 shadow-xl print:shadow-none">
      {/* Header */}
      <div className="flex items-center justify-between border-b-4 border-[#1a1a1a] pb-5">
        <div className="flex items-center gap-3">
          <ZentaraMark size={54} />
          <div>
            <div className="font-heading text-2xl font-extrabold tracking-tight text-neutral-900">Zentara</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6d1f]">As-Built &amp; Engineering Group Ltd</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-xl font-bold text-neutral-900">{r.job_number} PROGRESS REPORT</div>
          <div className="text-[13px] font-semibold text-neutral-600">{r.name}</div>
        </div>
      </div>

      {/* 1. Project Info */}
      <Section n="1" title="Project Information">
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
          <div><span className="font-semibold text-neutral-900">Job Number:</span> {r.job_number}</div>
          <div><span className="font-semibold text-neutral-900">Project Manager:</span> {r.project_manager || "—"}</div>
          <div><span className="font-semibold text-neutral-900">Client:</span> {r.client_name}</div>
          <div><span className="font-semibold text-neutral-900">Project Sponsor:</span> {r.project_sponsor || "—"}</div>
          {r.client_contact_name && <div><span className="font-semibold text-neutral-900">Client Contact:</span> {r.client_contact_name}</div>}
          {r.client_contact_phone && <div><span className="font-semibold text-neutral-900">Phone:</span> {r.client_contact_phone}</div>}
          {r.client_contact_email && <div><span className="font-semibold text-neutral-900">Email:</span> {r.client_contact_email}</div>}
          {r.client_address && <div><span className="font-semibold text-neutral-900">Address:</span> {r.client_address}</div>}
        </div>
      </Section>

      {/* 2. Document Control */}
      {r.revision_history?.length > 0 && (
        <Section n="2" title="Document Control — Revision History">
          <table className="w-full text-[12px]">
            <thead><tr className="text-left text-neutral-500"><th className="py-1">Date</th><th className="py-1">Version</th><th className="py-1">Description</th><th className="py-1">Author</th></tr></thead>
            <tbody>{r.revision_history.map((x, i) => <tr key={i} className="border-t border-neutral-200"><td className="py-1">{x.date}</td><td className="py-1 font-semibold text-[#8a6d1f]">{x.version}</td><td className="py-1">{x.description}</td><td className="py-1">{x.author}</td></tr>)}</tbody>
          </table>
        </Section>
      )}

      {/* 3. Project Organization */}
      {r.team?.length > 0 && (
        <Section n="3" title="Project Organization">
          <div className="grid grid-cols-2 gap-2">
            {r.team.map((m, i) => (
              <div key={i} className="rounded border border-neutral-200 px-3 py-2">
                <div className="font-semibold text-neutral-900">{m.name}</div>
                <div className="text-[12px] text-neutral-600">{m.role}{m.phone ? ` · ${m.phone}` : ""}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 4. Scope */}
      <Section n="4" title="Project Scope">
        <p>{r.client_name} has engaged Zentara to provide As-Built Drafting Services{r.scope_total ? ` covering ${r.scope_total} drawings` : ""}.</p>
      </Section>

      {/* 5. Project Updates */}
      <Section n={"5"} title={`Project Updates — ${r.overall_percent}% Complete`}>
        <div className="space-y-5">
          {r.areas.map((a) => (
            <div key={a.id}>
              <div className="mb-1 flex items-center justify-between">
                <h3 className="font-heading text-[14px] font-bold uppercase text-neutral-900">{a.name}</h3>
                <span className="text-[12px] text-neutral-600">Target {a.target_drawings || 0} · {a.percent}%</span>
              </div>
              <table className="w-full text-[11.5px]">
                <thead>
                  <tr className="bg-neutral-100 text-neutral-600">
                    <th className="px-2 py-1 text-left">Drawing #</th>
                    {COLS.map(([, l]) => <th key={l} className="px-1 py-1 text-center">{l}</th>)}
                    <th className="px-1 py-1 text-center">%</th>
                  </tr>
                </thead>
                <tbody>
                  {a.packages.map((pk) => (
                    <tr key={pk.id} className="border-b border-neutral-100">
                      <td className="px-2 py-1"><span className="font-semibold text-neutral-900">{pk.drawing_number}</span>{pk.description ? <span className="text-neutral-500"> — {pk.description}</span> : ""}</td>
                      {COLS.map(([k]) => <td key={k} className="px-1 py-1 text-center tabular-nums">{pk[k] || 0}</td>)}
                      <td className="px-1 py-1 text-center font-semibold tabular-nums text-[#8a6d1f]">{pk.percent}%</td>
                    </tr>
                  ))}
                  <tr className="bg-[#f7f3ec] font-semibold text-neutral-900">
                    <td className="px-2 py-1">Totals</td>
                    {COLS.map(([k]) => <td key={k} className="px-1 py-1 text-center tabular-nums">{a.totals[k] || 0}</td>)}
                    <td className="px-1 py-1 text-center tabular-nums text-[#8a6d1f]">{a.percent}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
          {/* Grand totals */}
          <table className="w-full text-[11.5px]">
            <tbody>
              <tr className="bg-[#1a1a1a] font-semibold text-white">
                <td className="px-2 py-1.5">Grand Totals</td>
                {COLS.map(([k]) => <td key={k} className="px-1 py-1.5 text-center tabular-nums">{r.grand[k] || 0}</td>)}
                <td className="px-1 py-1.5 text-center tabular-nums text-[#d8b968]">{r.overall_percent}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section n="6" title="Drawings Returned"><Bullets items={r.narrative?.drawings_returned} /></Section>
      <Section n="7" title="Outstanding Responses"><Bullets items={r.narrative?.outstanding_responses} /></Section>
      <Section n="8" title="Clarifications"><Bullets items={r.narrative?.clarifications} /></Section>
      <Section n="9" title="Risk to Cost & Schedule"><Bullets items={r.narrative?.risk_notes} /></Section>
      <Section n="10" title="Change Quotations"><Bullets items={r.narrative?.change_quotations} /></Section>

      <p className="mt-10 border-t border-neutral-200 pt-4 text-center text-[11px] italic text-neutral-500">
        Zentara As-Built &amp; Engineering Group Ltd — Excellence grounded in strength and integrity.
      </p>
    </div>
  );
}
